import { usePageSEO } from "@/hooks/usePageSEO";
import { useLanguage } from "@/contexts/LanguageContext";

const LAST_UPDATED = "April 22, 2026";

export default function PrivacyPolicy() {
  const { t } = useLanguage();
  usePageSEO({
    title: "Privacy Policy - ISO27001KIT",
    description:
      "How ISO27001KIT collects, uses, stores, and protects your personal information across our website, tools, AI Assistant, and Risk Copilot.",
    canonicalPath: "/privacy-policy",
  });

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 text-foreground">{t('privacy.title')}</h1>
        <div className="h-1 w-24 bg-primary mb-8" />

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8 text-foreground">
          <p className="text-sm text-muted-foreground">{t('privacy.lastUpdated')} {LAST_UPDATED}</p>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s1.title')}</h2>
            <p>{t('privacy.s1.p1')}</p>
            <p>
              {t('privacy.s1.p2')}{" "}
              <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
                contact@iso27001kit.com
              </a>
              .
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s2.title')}</h2>

            <h3 className="text-xl font-semibold">{t('privacy.s2.h1')}</h3>
            <p>{t('privacy.s2.p1')}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacy.s2.li1')}</li>
              <li>{t('privacy.s2.li2')}</li>
              <li>{t('privacy.s2.li3')}</li>
              <li>{t('privacy.s2.li4')}</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h2')}</h3>
            <p>{t('privacy.s2.p2')}</p>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h3')}</h3>
            <p>{t('privacy.s2.p3')}</p>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h4')}</h3>
            <p>{t('privacy.s2.p4')}</p>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h5')}</h3>
            <p>{t('privacy.s2.p5')}</p>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h6')}</h3>
            <p>{t('privacy.s2.p6')}</p>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h7')}</h3>
            <p>{t('privacy.s2.p7')}</p>

            <h3 className="text-xl font-semibold mt-4">{t('privacy.s2.h8')}</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacy.s2.li5')}</li>
              <li>{t('privacy.s2.li6')}</li>
              <li>{t('privacy.s2.li7')}</li>
              <li>{t('privacy.s2.li8')}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s3.title')}</h2>
            <p>{t('privacy.s3.intro')}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacy.s3.li1')}</li>
              <li>{t('privacy.s3.li2')}</li>
              <li>{t('privacy.s3.li3')}</li>
              <li>{t('privacy.s3.li4')}</li>
              <li>{t('privacy.s3.li5')}</li>
              <li>{t('privacy.s3.li6')}</li>
              <li>{t('privacy.s3.li7')}</li>
              <li>{t('privacy.s3.li8')}</li>
              <li>{t('privacy.s3.li9')}</li>
            </ul>
            <p>{t('privacy.s3.bases')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s4.title')}</h2>
            <p>{t('privacy.s4.intro')}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>{t('privacy.s4.li1.b')}</strong> {t('privacy.s4.li1.t')}</li>
              <li><strong>{t('privacy.s4.li2.b')}</strong> {t('privacy.s4.li2.t')}</li>
              <li><strong>{t('privacy.s4.li3.b')}</strong> {t('privacy.s4.li3.t')}</li>
              <li><strong>{t('privacy.s4.li4.b')}</strong> {t('privacy.s4.li4.t')}</li>
              <li><strong>{t('privacy.s4.li5.b')}</strong> {t('privacy.s4.li5.t')}</li>
              <li><strong>{t('privacy.s4.li6.b')}</strong> {t('privacy.s4.li6.t')}</li>
              <li><strong>{t('privacy.s4.li7.b')}</strong> {t('privacy.s4.li7.t')}</li>
            </ul>
            <p>{t('privacy.s4.outro')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s5.title')}</h2>
            <p>{t('privacy.s5.p1')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s6.title')}</h2>
            <p>{t('privacy.s6.intro')}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>{t('privacy.s6.li1.b')}</strong> {t('privacy.s6.li1.t')}</li>
              <li><strong>{t('privacy.s6.li2.b')}</strong> {t('privacy.s6.li2.t')}</li>
              <li><strong>{t('privacy.s6.li3.b')}</strong> {t('privacy.s6.li3.t')}</li>
            </ul>
            <p>{t('privacy.s6.outro')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s7.title')}</h2>
            <p>{t('privacy.s7.intro')}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacy.s7.li1')}</li>
              <li>{t('privacy.s7.li2')}</li>
              <li>{t('privacy.s7.li3')}</li>
              <li>{t('privacy.s7.li4')}</li>
              <li>{t('privacy.s7.li5')}</li>
            </ul>
            <p>{t('privacy.s7.outro')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s8.title')}</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>{t('privacy.s8.li1.b')}</strong> {t('privacy.s8.li1.t')}</li>
              <li><strong>{t('privacy.s8.li2.b')}</strong> {t('privacy.s8.li2.t')}</li>
              <li><strong>{t('privacy.s8.li3.b')}</strong> {t('privacy.s8.li3.t')}</li>
              <li><strong>{t('privacy.s8.li4.b')}</strong> {t('privacy.s8.li4.t')}</li>
              <li><strong>{t('privacy.s8.li5.b')}</strong> {t('privacy.s8.li5.t')}</li>
              <li><strong>{t('privacy.s8.li6.b')}</strong> {t('privacy.s8.li6.t')}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s9.title')}</h2>
            <p>{t('privacy.s9.intro')}</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>{t('privacy.s9.li1')}</li>
              <li>{t('privacy.s9.li2')}</li>
              <li>{t('privacy.s9.li3')}</li>
              <li>{t('privacy.s9.li4')}</li>
              <li>{t('privacy.s9.li5')}</li>
              <li>{t('privacy.s9.li6')}</li>
              <li>{t('privacy.s9.li7')}</li>
              <li>{t('privacy.s9.li8')}</li>
            </ul>
            <p>
              {t('privacy.s9.contactPre')}{" "}
              <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
                contact@iso27001kit.com
              </a>{" "}
              {t('privacy.s9.contactPost')}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s10.title')}</h2>
            <p>{t('privacy.s10.p1')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s11.title')}</h2>
            <p>{t('privacy.s11.p1')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s12.title')}</h2>
            <p>{t('privacy.s12.p1')}</p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-semibold">{t('privacy.s13.title')}</h2>
            <p>{t('privacy.s13.p1')}</p>
            <p className="font-semibold">
              <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
                contact@iso27001kit.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
